/**
 * VaultRun — reporting in. The vault door waits at the end of a long dark
 * corridor, light strips running along its walls; the camera pushes down it
 * until the door fills the screen, the unlock clip plays, and then the lock
 * that just opened is honoured: a gold line traces its medallion, a shine
 * crosses its word plate, sparks lift off it. Then onDone. A tap skips.
 *
 * The clip must be started inside the tap that reports in (browsers only let
 * a tap start video with sound), so the parent starts it and hands over the
 * element; this holds it on its first frame until the approach ends.
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { VAULT_CLIPS, LOCK_WORDS } from "./vault-clips";
import wallImg from "./vault-wall.webp";
import floorImg from "./vault-floor.webp";

/** How long the push down the corridor takes. */
const APPROACH_MS = 2600;
/** When, in the clip, the lock has swung open and its plate settled — the
 *  honour starts there, not at the clip's end (measured on the clips). */
const UNLOCKED_AT = 3.0;
/** How long the unlocked lock is honoured before moving on. */
const HONOUR_MS = 2800;
/** A clip that has not reached its unlock this long after arriving (stalled
 *  buffering, or paused under us) is honoured anyway: never stuck at the door. */
const STALL_MS = (UNLOCKED_AT + 3) * 1000;

/**
 * Where each lock's medallion and word plate sit on the clips' last frame
 * (720x1280): lock n (0 = Brave … 6 = Outlast). Odd locks have the medallion
 * on the right and the plate on the left; even ones the other way round.
 */
const LOCKS = [196, 314, 441, 568, 695, 828, 968].map((y, i) => {
  const right = i % 2 === 0;
  return { cx: right ? 441 : 282, cy: y, plateX: right ? 241 : 465 };
});
const MEDALLION_R = 54;
const PLATE = { w: 150, h: 46 };

/**
 * The corridor down to the door: walls, floor and ceiling running back to
 * it, the door (`children`, the clip) at the far end. The corridor is the
 * vault room itself: its wall (bronze stone, an amber light pillar) and glossy
 * floor, cut from the clip (vault-wall.webp / vault-floor.webp,
 * scripts/build-vault-corridor.py) and tiled down it, darkening with distance
 * from the door's light. `style` moves the camera.
 */
function Corridor({
  style,
  children,
}: {
  style?: CSSProperties;
  children: ReactNode;
}) {
  const wallStyle = {
    width: DEPTH,
    backgroundImage: `linear-gradient(90deg,rgba(0,0,0,.55),transparent 70%), url(${wallImg})`,
    backgroundSize: "auto, auto 100%",
    backgroundRepeat: "no-repeat, repeat-x",
  };
  const floorStyle = {
    height: DEPTH,
    backgroundImage: `linear-gradient(0deg,rgba(0,0,0,.6),transparent 75%), url(${floorImg})`,
    backgroundSize: "auto, 100% auto",
    backgroundRepeat: "no-repeat, repeat-y",
  };
  return (
    <div
      style={style}
      className="absolute inset-0 [transform-style:preserve-3d]"
    >
      <div
        style={wallStyle}
        className="absolute top-0 left-0 h-full origin-left rotate-y-90"
      />
      <div
        style={{ ...wallStyle, scale: "-1 1" }}
        className="absolute top-0 right-0 h-full origin-right -rotate-y-90"
      />
      <div
        style={floorStyle}
        className="absolute inset-x-0 bottom-0 origin-bottom rotate-x-90"
      />
      <div
        style={{ height: DEPTH }}
        className="absolute inset-x-0 top-0 origin-top -rotate-x-90 bg-[linear-gradient(90deg,transparent_47%,rgba(255,170,80,.35)_50%,transparent_53%),linear-gradient(180deg,#0a0705,#020101_70%)]"
      />
      {/* the door, at the far end */}
      <div
        style={{ transform: `translateZ(-${DEPTH}px)` }}
        className="absolute inset-0"
      >
        {children}
        {/* light spilling from the door's edges into the corridor */}
        <div className="pointer-events-none absolute inset-0 shadow-[0_0_120px_40px_rgba(255,160,60,.25)]" />
      </div>
    </div>
  );
}

/**
 * Their door as it stands, from HQ: down the hallway, the door at its end
 * with the locks they have opened so far (a still: the clip for the last
 * lock opened, held on its last frame, or the first clip on its first frame
 * when none are). The camera pushes down the hallway to it as when reporting
 * in, but the door itself holds still. A tap or × closes it.
 */
export function DoorView({
  locksOpen,
  onClose,
  autoCloseMs,
}: {
  locksOpen: number;
  onClose: () => void;
  /** shown for a moment on its own (the well done), then closes itself */
  autoCloseMs?: number;
}) {
  useEffect(() => {
    if (!autoCloseMs) return;
    const t = window.setTimeout(onClose, autoCloseMs);
    return () => window.clearTimeout(t);
  }, [autoCloseMs, onClose]);
  const open = Math.min(Math.max(locksOpen, 0), VAULT_CLIPS.length);
  const src = VAULT_CLIPS[Math.max(open - 1, 0)];
  // Escape closes it, as × does
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Your door, ${open} of ${VAULT_CLIPS.length} locks open`}
      onClick={onClose}
      style={{ "--sm-ring": "#d6a94e" } as CSSProperties}
      className="fixed inset-0 z-[60] animate-[smFadeIn_.3s_ease_both] cursor-pointer overflow-hidden bg-black"
    >
      <div className="absolute inset-0 [perspective:700px]">
        {/* down the hallway to it, as when reporting in; the door itself
            holds still */}
        <Corridor
          style={{
            animation: `smCorridor ${APPROACH_MS}ms cubic-bezier(.55,0,.3,1) both`,
          }}
        >
          <video
            src={src}
            muted
            playsInline
            preload="auto"
            // a still of where it stands: the end of the last lock's clip
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              v.currentTime = open > 0 ? Math.max(0, v.duration - 0.05) : 0;
            }}
            className="absolute inset-0 h-full w-full bg-black object-cover"
          />
        </Corridor>
      </div>
      <div
        style={{ animation: `smHazeLift 500ms ease-in ${APPROACH_MS}ms both` }}
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_30%_22%_at_50%_45%,rgba(255,170,80,.18),transparent),radial-gradient(ellipse_75%_65%_at_50%_45%,transparent_50%,rgba(0,0,0,.75))]"
      />
      <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,.08)_0_1px,transparent_1px_3px)]" />
      <div className="pointer-events-none absolute inset-x-0 top-[max(18px,3dvh)] text-center">
        <span className="inline-block rounded-full bg-black/75 px-4 py-1.5 font-mono text-[11px] tracking-[.28em] text-[var(--sm-ring)] uppercase [text-shadow:0_0_8px_var(--sm-ring)]">
          Your door · {open}/{VAULT_CLIPS.length} locks open
        </span>
      </div>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-3 right-3 grid size-10 cursor-pointer place-items-center rounded-full border border-[var(--sm-ring)]/50 bg-black/60 text-[22px] leading-none text-[#f4eddb]"
      >
        ×
      </button>
      <style>{`
        @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes smHazeLift{to{opacity:0}}
        @keyframes smCorridor{from{transform:translateZ(0)}to{transform:translateZ(${DEPTH}px)}}
      `}</style>
    </div>
  );
}

/** Corridor depth, in px: how far the door stands down it. */
const DEPTH = 1800;

export function VaultRun({
  src,
  lock,
  active,
  videoRef,
  playingRef,
  onDone,
}: {
  src: string;
  /** which lock this clip opens (0-based) */
  lock: number;
  active: boolean;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** set when the clip is played on arrival: the parent's tap-time hold
   *  must not pause it after that */
  playingRef: RefObject<boolean>;
  onDone: () => void;
}) {
  const [arrived, setArrived] = useState(false);
  const [honour, setHonour] = useState(false);
  const at = Math.min(Math.max(lock, 0), LOCKS.length - 1);
  const l = LOCKS[at];
  const lockNo = at + 1;
  // the word on its plate
  const lockName = LOCK_WORDS[at];
  // the parent re-renders every second (its clock): keep the latest onDone
  // without restarting the timers below
  const doneRef = useRef(onDone);
  useEffect(() => {
    doneRef.current = onDone;
  });

  // the push down the corridor, then the clip
  useEffect(() => {
    if (!active) return;
    let stall: ReturnType<typeof setTimeout> | undefined;
    const t = setTimeout(() => {
      setArrived(true);
      playingRef.current = true;
      const v = videoRef.current;
      if (v) {
        v.currentTime = 0;
        v.play().catch(() => setHonour(true));
      }
      stall = setTimeout(() => setHonour(true), STALL_MS);
    }, APPROACH_MS);
    return () => {
      clearTimeout(t);
      clearTimeout(stall);
    };
  }, [active, videoRef, playingRef]);

  // the lock honoured, then on
  useEffect(() => {
    if (!honour) return;
    const t = setTimeout(() => doneRef.current(), HONOUR_MS);
    return () => clearTimeout(t);
  }, [honour]);

  return (
    <div
      onClick={active ? onDone : undefined}
      className={`absolute inset-0 z-[30] cursor-pointer overflow-hidden bg-black transition-opacity duration-300 ${
        // invisible, not just see-through: iOS Safari lets a transparent
        // layer with a video in it swallow swipes, so the report window
        // and mission under it could not scroll
        active ? "opacity-100" : "pointer-events-none invisible opacity-0"
      }`}
    >
      {/* the footage, in its own colour */}
      <div
        className={`absolute inset-0 [perspective:700px] ${
          honour && arrived ? "animate-[smGlitch_.4s_steps(2,end)_both]" : ""
        }`}
      >
        <Corridor
          style={
            active
              ? {
                  animation: `smCorridor ${APPROACH_MS}ms cubic-bezier(.55,0,.3,1) both`,
                }
              : undefined
          }
        >
          <video
            ref={videoRef}
            src={src}
            playsInline
            preload="auto"
            onTimeUpdate={(e) => {
              if (e.currentTarget.currentTime >= UNLOCKED_AT) setHonour(true);
            }}
            onEnded={() => setHonour(true)}
            className="absolute inset-0 h-full w-full bg-black object-cover"
          />
        </Corridor>
      </div>

      {/* warm haze round the door, and the dark closing in at the edges —
          both lift as the camera arrives */}
      <div
        className={`pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_30%_22%_at_50%_45%,rgba(255,170,80,.22),transparent),radial-gradient(ellipse_75%_65%_at_50%_45%,transparent_50%,rgba(0,0,0,.75))] transition-opacity ease-in ${
          arrived ? "opacity-0 duration-500" : "opacity-100 duration-0"
        }`}
      />
      {/* the feed: faint scanlines, a scan line sweeping down */}
      <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,.08)_0_1px,transparent_1px_3px)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[9%] animate-[smScan_3.2s_linear_infinite] bg-[linear-gradient(180deg,transparent,color-mix(in_srgb,var(--sm-ring)_22%,transparent)_85%,color-mix(in_srgb,var(--sm-ring)_70%,transparent))] opacity-30" />

      {/* the HUD: brackets in the corners, the readout, the progress */}
      {active ? (
        <div className="pointer-events-none absolute inset-0 font-mono text-[var(--sm-ring)] uppercase">
          {(
            [
              "top-4 left-4 border-t-2 border-l-2",
              "top-4 right-4 border-t-2 border-r-2",
              "bottom-4 left-4 border-b-2 border-l-2",
              "right-4 bottom-4 border-r-2 border-b-2",
            ] as const
          ).map((c) => (
            <span
              key={c}
              className={`absolute size-7 border-[var(--sm-ring)] opacity-80 [filter:drop-shadow(0_0_6px_var(--sm-ring))] ${c}`}
            />
          ))}
          <div className="absolute top-[max(16px,3dvh)] inset-x-12 text-center text-[11px] tracking-[.28em] [text-shadow:0_0_8px_var(--sm-ring)]">
            Vault access · lock {lockNo}/7
          </div>
          <div className="absolute inset-x-8 bottom-[max(36px,6dvh)] text-center">
            <div className="text-[12.5px] tracking-[.22em] [text-shadow:0_0_8px_var(--sm-ring)]">
              {honour && arrived ? (
                <>Lock open · Proved {lockName}</>
              ) : arrived ? (
                // the lock's word stays secret until it has opened
                <>
                  Disengaging · lock {lockNo}
                  <span className="animate-[smBlinkCursor_.8s_steps(1)_infinite]">
                    _
                  </span>
                </>
              ) : (
                <>Approaching vault…</>
              )}
            </div>
            <div className="mx-auto mt-2 h-[3px] w-[70%] overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--sm-ring)_20%,transparent)]">
              <div
                style={{
                  animation: arrived
                    ? `smFill ${UNLOCKED_AT}s linear both`
                    : `smFill ${APPROACH_MS}ms linear both`,
                }}
                key={arrived ? "lock" : "walk"}
                className="h-full origin-left bg-[var(--sm-ring)] shadow-[0_0_8px_var(--sm-ring)]"
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* the reticle, locking onto the lock while it turns */}
      {arrived && !honour ? (
        <svg
          viewBox="0 0 720 1280"
          preserveAspectRatio="xMidYMid slice"
          className="pointer-events-none absolute inset-0 size-full text-[var(--sm-ring)]"
        >
          <g
            style={{ transformOrigin: `${l.cx}px ${l.cy}px` }}
            className="animate-[smReticleIn_.6s_cubic-bezier(.2,.8,.3,1)_both]"
          >
            <circle
              cx={l.cx}
              cy={l.cy}
              r={MEDALLION_R + 18}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeDasharray="14 10"
              style={{ transformOrigin: `${l.cx}px ${l.cy}px` }}
              className="animate-[smSpin_4s_linear_infinite]"
            />
            {[0, 90, 180, 270].map((a) => (
              <line
                key={a}
                x1={l.cx}
                y1={l.cy - MEDALLION_R - 34}
                x2={l.cx}
                y2={l.cy - MEDALLION_R - 12}
                stroke="currentColor"
                strokeWidth="3"
                transform={`rotate(${a} ${l.cx} ${l.cy})`}
              />
            ))}
          </g>
        </svg>
      ) : null}

      {/* unlocked: a flash in the case's light, the lock stamped open (the
          door itself stays shut until all seven are) */}
      {honour && arrived ? (
        <>
          <div className="pointer-events-none absolute inset-0 animate-[smFlashOut_.6s_ease-out_both] bg-[var(--sm-ring)]" />
          <div className="pointer-events-none absolute inset-x-0 top-[40%] text-center">
            <span className="inline-block animate-[smStamp_.45s_cubic-bezier(.2,1.4,.4,1)_both] border-y-2 border-[var(--sm-ring)] bg-black/70 px-5 py-1.5 font-mono text-[20px] font-bold tracking-[.3em] text-[var(--sm-ring)] [text-shadow:0_0_12px_var(--sm-ring)]">
              LOCK {lockNo} OF 7 OPEN
            </span>
          </div>
        </>
      ) : null}

      {/* the lock that just opened, honoured — drawn in the clip's own
          coordinates, cropped exactly as the video is (slice = cover) */}
      {honour && arrived ? (
        <svg
          viewBox="0 0 720 1280"
          preserveAspectRatio="xMidYMid slice"
          className="pointer-events-none absolute inset-0 size-full"
        >
          <defs>
            <linearGradient id="smShine" x1="0" x2="1">
              <stop offset="0" stopColor="#fff3cf" stopOpacity="0" />
              <stop offset=".5" stopColor="#fff3cf" stopOpacity=".95" />
              <stop offset="1" stopColor="#fff3cf" stopOpacity="0" />
            </linearGradient>
            <clipPath id="smPlate">
              <rect
                x={l.plateX - PLATE.w / 2}
                y={l.cy - PLATE.h / 2}
                width={PLATE.w}
                height={PLATE.h}
                rx="6"
              />
            </clipPath>
            <filter id="smGlow">
              <feGaussianBlur stdDeviation="4" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* the gold trace round the medallion */}
          <circle
            cx={l.cx}
            cy={l.cy}
            r={MEDALLION_R + 6}
            fill="none"
            style={{ stroke: "var(--sm-ring)" }}
            strokeWidth="4"
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray="1"
            filter="url(#smGlow)"
            className="animate-[smTrace_.6s_ease-out_both]"
            transform={`rotate(-90 ${l.cx} ${l.cy})`}
          />
          <circle
            cx={l.cx}
            cy={l.cy}
            r={MEDALLION_R + 6}
            style={{
              fill: "color-mix(in srgb,var(--sm-ring) 22%,transparent)",
            }}
            className="animate-[smPulse_1.6s_ease-in-out_.6s_infinite]"
          />
          {/* the shine across the word plate */}
          <g clipPath="url(#smPlate)">
            <rect
              x={l.plateX - PLATE.w / 2 - 80}
              y={l.cy - PLATE.h}
              width="70"
              height={PLATE.h * 2}
              fill="url(#smShine)"
              transform={`skewX(-20)`}
              className="animate-[smSweep_1.4s_ease-in-out_.2s_infinite]"
            />
          </g>
          {/* sparks lifting off the medallion */}
          {[-70, -40, -10, 20, 50, 80, 110, 140].map((deg, i) => {
            const t = ((deg - 90) * Math.PI) / 180;
            const r = MEDALLION_R + 10;
            return (
              <circle
                key={deg}
                cx={l.cx + r * Math.cos(t)}
                cy={l.cy + r * Math.sin(t)}
                r="3.2"
                fill="#fff0c2"
                filter="url(#smGlow)"
                style={
                  {
                    "--sx": `${Math.cos(t) * 46}px`,
                    "--sy": `${Math.sin(t) * 46}px`,
                    animationDelay: `${300 + i * 40}ms`,
                  } as CSSProperties
                }
                className="animate-[smSpark_.9s_ease-out_both]"
              />
            );
          })}
        </svg>
      ) : null}

      <style>{`
        @keyframes smScan{from{translate:0 -100%}to{translate:0 1120%}}
        @keyframes smFill{from{scale:0 1}to{scale:1 1}}
        @keyframes smBlinkCursor{50%{opacity:0}}
        @keyframes smSpin{to{rotate:360deg}}
        @keyframes smReticleIn{from{opacity:0;scale:1.8}to{opacity:1;scale:1}}
        @keyframes smGlitch{0%{translate:0 0;filter:none}25%{translate:-6px 2px;filter:contrast(1.5) brightness(1.4) hue-rotate(40deg)}50%{translate:5px -3px;filter:contrast(1.25) brightness(1.15)}75%{translate:-2px 1px}100%{translate:0 0;filter:none}}
        @keyframes smFlashOut{from{opacity:.55}to{opacity:0}}
        @keyframes smStamp{from{opacity:0;scale:1.8}to{opacity:1;scale:1}}
        @keyframes smCorridor{from{transform:translateZ(0)}to{transform:translateZ(${DEPTH}px)}}
        @keyframes smTrace{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}
        @keyframes smPulse{0%,100%{opacity:.25}50%{opacity:.9}}
        @keyframes smSweep{from{translate:0 0}to{translate:${PLATE.w + 160}px 0}}
        @keyframes smSpark{0%{opacity:0;translate:0 0}25%{opacity:1}100%{opacity:0;translate:var(--sx) var(--sy)}}
      `}</style>
    </div>
  );
}
